import { Link } from "react-router-dom"
import { Check, Crown, Zap, Gift } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useLang } from "../context/LangContext"

const T = {
  en: {
    badge: "Pricing",
    title: "Simple, transparent pricing",
    sub: "Start free. Upgrade when you're ready to unlock the full community and premium tools.",
    free: "Free",
    freeSub: "Always free",
    freeBullets: [
      "All 4 built-in tools (calculator, time tracker, coin market, calendar)",
      "Public pages: About, Alliances",
      "Account & profile",
    ],
    freeCta: "Get started",
    plus: "Plus",
    plusSub: "per month",
    plusBullets: [
      "Everything in Free",
      "Access to private Discord community",
      "Specialty channels (Tech, Finance, Legal, Logistics, Procedures)",
      "Early access to new features",
      "24/7 community support",
    ],
    plusCta: "Upgrade to Plus",
    premium: "Premium",
    premiumSub: "per month",
    premiumBullets: [
      "Everything in Plus",
      "Exclusive 1:1 Mentorship discounts",
      "Access to premium technology tools",
      "Priority invitations to in-person events",
      "Premium Discord channels",
    ],
    premiumCta: "Go Premium",
    recommended: "Most popular",
    loginPrompt: "Sign in to upgrade →",
    faqTitle: "Common questions",
    faqs: [
      { q: "Can I cancel anytime?", a: "Yes. Cancel at any time from your dashboard. You keep access until the end of the billing period." },
      { q: "Is there a free trial?", a: "The Free plan is free forever. Paid plans include all features from day one — no trial required." },
      { q: "How are payments processed?", a: "Payments are handled securely by Stripe. We never store your card details." },
      { q: "What is the difference between Plus and Premium?", a: "Plus gives you full community access. Premium adds exclusive mentorship benefits and priority event invitations." },
    ],
  },
  es: {
    badge: "Precios",
    title: "Precios simples y transparentes",
    sub: "Empieza gratis. Actualiza cuando estés listo para acceder a la comunidad completa y herramientas premium.",
    free: "Gratis",
    freeSub: "Siempre gratis",
    freeBullets: [
      "Las 4 herramientas incluidas (calculadora, time tracker, mercado cripto, calendario)",
      "Páginas públicas: Nosotros, Aliados",
      "Cuenta y perfil",
    ],
    freeCta: "Comenzar gratis",
    plus: "Plus",
    plusSub: "por mes",
    plusBullets: [
      "Todo lo del plan Gratis",
      "Acceso a la comunidad privada de Discord",
      "Canales de especialidad (Tech, Finanzas, Legal, Logística, Procedimientos)",
      "Acceso anticipado a nuevas funciones",
      "Soporte comunitario 24/7",
    ],
    plusCta: "Mejorar a Plus",
    premium: "Premium",
    premiumSub: "por mes",
    premiumBullets: [
      "Todo lo del plan Plus",
      "Descuentos exclusivos en Mentorías 1:1",
      "Acceso a herramientas tecnológicas premium",
      "Invitaciones prioritarias a eventos presenciales",
      "Canales Discord premium",
    ],
    premiumCta: "Obtener Premium",
    recommended: "Más popular",
    loginPrompt: "Inicia sesión para mejorar →",
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Puedo cancelar en cualquier momento?", a: "Sí. Cancela cuando quieras desde tu dashboard. Mantienes el acceso hasta el fin del período de facturación." },
      { q: "¿Hay período de prueba?", a: "El plan Gratis es gratuito para siempre. Los planes de pago incluyen todas las funciones desde el primer día." },
      { q: "¿Cómo se procesan los pagos?", a: "Los pagos son manejados de forma segura por Stripe. Nunca almacenamos los datos de tu tarjeta." },
      { q: "¿Cuál es la diferencia entre Plus y Premium?", a: "Plus da acceso completo a la comunidad. Premium agrega beneficios exclusivos de mentoría e invitaciones prioritarias a eventos." },
    ],
  },
}

const STRIPE_LINKS = {
  plus:    "https://buy.stripe.com/bJedRa4zU84t5WEenO9Zm0i",
  premium: "https://buy.stripe.com/dRm6oI2rM84t84MgvW9Zm0j",
}

export default function PricingPage() {
  const { user } = useAuth()
  const { lang } = useLang()
  const t = T[lang]

  const handleCheckout = (plan: "plus" | "premium") => {
    if (!user) { window.location.href = "/login"; return }
    window.location.href = STRIPE_LINKS[plan]
  }

  const checkBullet = "flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300"
  const dot = "mt-1.5 h-4 w-4 text-green-500 flex-shrink-0"

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
      <div className="max-w-6xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-block text-xs font-medium tracking-widest uppercase text-gray-400 mb-4">{t.badge}</div>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-4 text-gray-500 dark:text-gray-400 leading-relaxed">{t.sub}</p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">

          {/* FREE */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8 bg-white dark:bg-neutral-950">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="h-5 w-5 text-gray-400" />
              <div className="text-lg font-semibold">{t.free}</div>
            </div>
            <div className="text-sm text-gray-400 mb-6">{t.freeSub}</div>
            <div className="flex items-end gap-1 mb-8">
              <span className="text-4xl font-semibold">$0</span>
            </div>
            <ul className="space-y-3 mb-8">
              {t.freeBullets.map((b) => (
                <li key={b} className={checkBullet}>
                  <Check className={dot} />
                  {b}
                </li>
              ))}
            </ul>
            <Link to="/register" className="block w-full text-center rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-neutral-900 transition">
              {t.freeCta}
            </Link>
          </div>

          {/* PLUS — recommended */}
          <div className="rounded-2xl border-2 border-[#2563EB] p-8 bg-white dark:bg-neutral-950 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-[#2563EB] text-white text-xs font-medium px-3 py-1 rounded-full">{t.recommended}</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-5 w-5 text-blue-500" />
              <div className="text-lg font-semibold">{t.plus}</div>
            </div>
            <div className="text-sm text-gray-400 mb-6">{t.plusSub}</div>
            <div className="flex items-end gap-1 mb-8">
              <span className="text-4xl font-semibold">$9.99</span>
              <span className="text-sm text-gray-400 mb-1">/{t.plusSub}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {t.plusBullets.map((b) => (
                <li key={b} className={checkBullet}>
                  <Check className={dot} />
                  {b}
                </li>
              ))}
            </ul>
            {user ? (
              <button
                onClick={() => handleCheckout("plus")}
                className="w-full rounded-xl bg-black dark:bg-white text-white dark:text-black px-4 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                {t.plusCta}
              </button>
            ) : (
              <Link to="/login" className="block w-full text-center rounded-xl bg-black dark:bg-white text-white dark:text-black px-4 py-3 text-sm font-semibold hover:opacity-90 transition">
                {t.loginPrompt}
              </Link>
            )}
          </div>

          {/* PREMIUM */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8 bg-neutral-950 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="h-5 w-5 text-yellow-400" />
              <div className="text-lg font-semibold">{t.premium}</div>
            </div>
            <div className="text-sm text-gray-400 mb-6">{t.premiumSub}</div>
            <div className="flex items-end gap-1 mb-8">
              <span className="text-4xl font-semibold">$29.99</span>
              <span className="text-sm text-gray-400 mb-1">/{t.premiumSub}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {t.premiumBullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-gray-300">
                  <Check className="mt-1.5 h-4 w-4 text-green-400 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            {user ? (
              <button
                onClick={() => handleCheckout("premium")}
                className="w-full rounded-xl bg-white text-black px-4 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                {t.premiumCta}
              </button>
            ) : (
              <Link to="/login" className="block w-full text-center rounded-xl bg-white text-black px-4 py-3 text-sm font-semibold hover:opacity-90 transition">
                {t.loginPrompt}
              </Link>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold tracking-tight text-center mb-10">{t.faqTitle}</h2>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {t.faqs.map((faq) => (
              <div key={faq.q} className="py-6">
                <div className="font-medium mb-2 text-black dark:text-white">{faq.q}</div>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
